import cn from "classnames";
import { ChartRangeType } from "components";
import { BoxShadowLayout } from "layouts";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { DateRangeInput, Select } from "ui";

import { FinancialComparison } from "../../../../constants";
import { PeriodToolbarProps } from "./PeriodToolbar.types";

import styles from "./PeriodToolbar.module.scss";

const PRESET_OPTIONS = [
  { value: ChartRangeType.Week, label: "Week" },
  { value: ChartRangeType.Month, label: "Month" },
  { value: ChartRangeType.Year, label: "Year" },
  { value: ChartRangeType.Custom, label: "Custom" }
];

const COMPARISON_OPTIONS = [
  { value: FinancialComparison.None, label: "No comparison" },
  { value: FinancialComparison.PreviousPeriod, label: "Previous period" },
  {
    value: FinancialComparison.SamePeriodLastYear,
    label: "Same period last year"
  },
  { value: FinancialComparison.Custom, label: "Custom range" }
];

const formatWindowLabel = (from: string, to: string) =>
  `${moment(from).format("MMM DD, YYYY")} – ${moment(to).format("MMM DD, YYYY")}`;

const PeriodToolbar = ({
  preset,
  onPresetChange,
  customRange,
  onCustomRangeChange,
  comparison,
  onComparisonChange,
  customComparisonRange,
  onCustomComparisonRangeChange,
  window,
  comparisonWindow
}: PeriodToolbarProps) => {
  const { t } = useTranslation();

  return (
    <BoxShadowLayout className={styles.toolbar}>
      <div className={styles.toolbar__tabs}>
        {PRESET_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={cn(styles.toolbar__tab, {
              [styles["toolbar__tab--active"]]: preset === option.value
            })}
            onClick={() => onPresetChange(option.value)}
          >
            {t(option.label)}
          </button>
        ))}
      </div>

      {preset === ChartRangeType.Custom ? (
        <div className={styles.toolbar__picker}>
          <DateRangeInput
            value={customRange}
            onChange={(value) => {
              if (Array.isArray(value) && value[0] && value[1]) {
                onCustomRangeChange([value[0], value[1]]);
              }
            }}
          />
        </div>
      ) : (
        window && (
          <div className={styles.toolbar__window}>
            {formatWindowLabel(window.from, window.to)}
          </div>
        )
      )}

      <div className={styles.toolbar__spacer} />

      <div className={styles.toolbar__compare}>
        <Select
          label="Compare to"
          value={comparison}
          options={COMPARISON_OPTIONS}
          onChange={(value) => onComparisonChange(value as FinancialComparison)}
        />
      </div>

      {comparison === FinancialComparison.Custom && (
        <div className={styles.toolbar__picker}>
          <DateRangeInput
            value={customComparisonRange}
            onChange={(value) => {
              if (Array.isArray(value) && value[0] && value[1]) {
                onCustomComparisonRangeChange([value[0], value[1]]);
              }
            }}
          />
        </div>
      )}

      {comparison !== FinancialComparison.None && comparisonWindow && (
        <div className={styles.toolbar__comparedTo}>
          {formatWindowLabel(comparisonWindow.from, comparisonWindow.to)}
        </div>
      )}
    </BoxShadowLayout>
  );
};

export default PeriodToolbar;
